import { useEffect, useState } from 'react';
import CategoriesAPI from '@/api/CategoriesAPI';
import { ICategoryFilter } from '@/models/categories.model';
import { FilterType, IFilterValueLocale } from '@/models/filters.model';

export interface ISeoFilterValue {
  id: string;
  slug: string;
  locale: IFilterValueLocale;
}

export interface ISeoFilterGroup {
  filter: ICategoryFilter;
  values: ISeoFilterValue[];
  seoValueIds: string[];
}

interface IUseCategorySeoFiltersResult {
  groups: ISeoFilterGroup[];
  isLoading: boolean;
}

// Loads a category's filters together with the filter values bound to it.
// The admin category-details endpoint already embeds each filter's bound values
// (category_filter.values), so a single request is enough — no per-filter calls.
// Ignores stale responses when the selected category changes mid-flight.
export const useCategorySeoFilters = (categoryId: string | null): IUseCategorySeoFiltersResult => {
  const [groups, setGroups] = useState<ISeoFilterGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!categoryId) {
      setGroups([]);
      return;
    }

    let isActive = true;
    setIsLoading(true);

    (async () => {
      try {
        const response = await CategoriesAPI.getCategoryDetails(categoryId);

        if (!isActive) return;

        // Slider/range filters can never be SEO landings (ranges live only in the
        // query string and are never indexable), so they are excluded here.
        const filters: ICategoryFilter[] = (response.data.data.filters ?? []).filter(
          (filter) => filter.type !== FilterType.SLIDER,
        );

        setGroups(
          filters.map((filter) => ({
            filter,
            values: (filter.values ?? []).flatMap((value) =>
              value.id ? [{ id: value.id, slug: value.slug, locale: value.locale }] : [],
            ),
            seoValueIds: filter.seo_filters ?? [],
          })),
        );
      } catch {
        if (isActive) setGroups([]);
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [categoryId]);

  return { groups, isLoading };
};
